import {
  FormControl,
  MenuItem,
  Select,
  Skeleton,
  Typography,
} from "@mui/material";
import { useGetSettlementPeriodsQuery } from "Api/Queries/Transactions/useGetSettlementPeriodsQuery";
import { Resources, useResource } from "Translations/Resources";
import { formatPeriod } from "Utils/SettlementUtils";

type Props = {
  value: number | null;
  onChange: (value: number) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: "small" | "medium";
};

export const SettlementPeriodSelector = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  size = "medium",
}: Props) => {
  const { t } = useResource();
  const { data: settlementPeriods = [], isLoading } =
    useGetSettlementPeriodsQuery();

  if (isLoading) {
    return (
      <Skeleton
        variant="rectangular"
        sx={{
          minWidth: 200,
          height: ({ spacing }) => spacing(13),
        }}
      />
    );
  }

  if (settlementPeriods.length === 0) {
    return (
      <Typography>
        {t(
          Resources.Components.Shared.SettlementPeriodSelector
            .NoSettlementPeriods,
        )}
      </Typography>
    );
  }

  return (
    <FormControl sx={{ minWidth: 200 }}>
      <Select
        size={size}
        sx={
          size === "medium"
            ? { height: ({ spacing }) => spacing(13) }
            : undefined
        }
        value={value ?? ""}
        onChange={(e) => onChange(Number(e.target.value))}
        displayEmpty
        disabled={disabled}
      >
        <MenuItem value="" disabled>
          {placeholder ??
            t(Resources.Components.Shared.SettlementPeriodSelector.Placeholder)}
        </MenuItem>
        {settlementPeriods.map(
          ({ settlementPeriodID, recordSettlementDate }, index) => {
            const previousSettlementDate =
              settlementPeriods[index + 1]?.recordSettlementDate;
            return (
              <MenuItem key={settlementPeriodID} value={settlementPeriodID}>
                {formatPeriod(recordSettlementDate, previousSettlementDate)}
              </MenuItem>
            );
          },
        )}
      </Select>
    </FormControl>
  );
};
